"use client";

import { useEffect, useMemo, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Badge } from "@/shadcn-components/ui/badge";
import { ScrollArea } from "@/shadcn-components/ui/scroll-area";
import CustomIndicatorInputsForm, {
  defaultValuesFromParsedInputs,
} from "./CustomIndicatorInputsForm";
import type { CustomIndicatorRecord } from "@/lib/custom-indicators/types";
import { ensureCompiledFromBuild } from "@/lib/custom-indicators/hydrate-build";
import { useIndicatorCompileStore } from "@/lib/custom-indicators/runtime/compile-store";
import {
  compileInputsToCatalogParameters,
  compileInputsToParsedInputs,
  parseInputs,
  parsedInputsToCatalogParameters,
} from "@/lib/custom-indicators/indicator-source";
import type { IndicatorInstance } from "@/lib/chart-indicators/registry";
import type { CatalogParameter } from "@/lib/chart-indicators/catalog";
import CustomIndicatorIcon from "./CustomIndicatorIcon";

interface CustomIndicatorConfigProps {
  record: CustomIndicatorRecord;
  /** Existing instance when editing an active custom indicator on chart */
  existingInstance?: IndicatorInstance | null;
  onBack: () => void;
  onApply: (instance: IndicatorInstance) => void;
}

export default function CustomIndicatorConfig({
  record,
  existingInstance,
  onBack,
  onApply,
}: CustomIndicatorConfigProps) {
  const compileResult = useIndicatorCompileStore((s) => s.byProjectId[record.id]);

  useEffect(() => {
    void ensureCompiledFromBuild(record.id);
  }, [record.id]);
  const iconDataUrl = compileResult?.icon?.dataUrl ?? record.iconDataUrl;
  const parsed = useMemo(() => parseInputs(record.code), [record.code]);
  const formInputs = useMemo(() => {
    if (compileResult?.success && compileResult.inputs?.length) {
      return compileInputsToParsedInputs(compileResult.inputs);
    }
    return parsed;
  }, [compileResult, parsed]);
  const parameters: CatalogParameter[] = useMemo(() => {
    if (compileResult?.success && compileResult.inputs?.length) {
      return compileInputsToCatalogParameters(compileResult.inputs);
    }
    return parsedInputsToCatalogParameters(parsed);
  }, [compileResult, parsed]);

  const [applyError, setApplyError] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  const [values, setValues] = useState<Record<string, number | string>>(() => {
    if (existingInstance?.settings?.values) {
      return { ...existingInstance.settings.values };
    }
    return defaultValuesFromParsedInputs(formInputs);
  });

  const createsNewChart =
    existingInstance?.createsNewChart ??
    !(compileResult?.overlay ?? record.overlay);

  const handleApply = async () => {
    setApplyError(null);
    setIsApplying(true);
    try {
      const loaded = await ensureCompiledFromBuild(record.id);
      if (!loaded?.success) {
        setApplyError(
          "Could not load the compiled indicator. Wait a moment after import, then try again."
        );
        return;
      }
      const instance: IndicatorInstance = {
        id:
          existingInstance?.id ??
          `id_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
        indicatorId: `custom_${record.id}`,
        name: record.name,
        createsNewChart,
        category: "custom",
        parameters,
        settings: { values, styles: {} },
        visible: existingInstance?.visible ?? true,
      };
      onApply(instance);
    } catch (e) {
      setApplyError(
        e instanceof Error ? e.message : "Failed to prepare indicator for chart"
      );
    } finally {
      setIsApplying(false);
    }
  };

  const isEditing = Boolean(existingInstance);

  return (
    <div className="h-full flex flex-col min-h-0 bg-background text-foreground">
      <div className="flex items-start gap-2 px-3 py-2.5 border-b border-border shrink-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="h-8 w-8 shrink-0 mt-0.5"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0 pr-1 flex items-start gap-2">
          <CustomIndicatorIcon
            name={record.name}
            iconDataUrl={iconDataUrl}
            size={40}
            className="mt-0.5"
          />
          <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
            {isEditing ? "Edit custom indicator" : "Add custom indicator"}
          </p>
          <h1 className="text-sm font-semibold truncate leading-tight">
            {record.name}
          </h1>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            <Badge variant="secondary" className="text-[10px] font-normal h-5">
              v{record.version}
            </Badge>
            <Badge variant="outline" className="text-[10px] font-normal h-5">
              Custom
            </Badge>
          </div>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="p-3 space-y-4">
          <p className="text-xs text-muted-foreground rounded-lg border border-border bg-muted/30 px-3 py-2">
            Chart placement:{" "}
            <strong className="text-foreground">
              {createsNewChart ? "Separate pane" : "Overlay on price"}
            </strong>
            {" "}
            (from indicator metadata — change in editor via{" "}
            <code className="text-[10px]">[Indicator(Overlay=…)]</code> and
            recompile)
          </p>

          {applyError ? (
            <p className="text-xs text-destructive rounded-lg border border-destructive/30 px-3 py-2">
              {applyError}
            </p>
          ) : null}
          {!compileResult?.success && !applyError ? (
            <p className="text-xs text-amber-600 dark:text-amber-500 rounded-lg border border-amber-500/30 px-3 py-2">
              Loading compiled build from import… If this persists, re-import the
              .vybeind file or open Vybe Studio and try again.
            </p>
          ) : null}

          <CustomIndicatorInputsForm
            parsedInputs={formInputs}
            values={values}
            onValueChange={(n, v) =>
              setValues((prev) => ({ ...prev, [n]: v }))
            }
          />
        </div>
      </ScrollArea>

      <div className="p-3 border-t border-border shrink-0 flex gap-2">
        <Button variant="outline" className="flex-1" size="sm" onClick={onBack}>
          Cancel
        </Button>
        <Button
          className="flex-1"
          size="sm"
          disabled={isApplying}
          onClick={() => void handleApply()}
        >
          {isApplying ? "Loading…" : isEditing ? "Apply" : "Add to chart"}
        </Button>
      </div>
    </div>
  );
}
